import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { InAppBrowser, InAppBrowserObject } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { MenuController, NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { of, Subject, timer } from 'rxjs';
import { first, map, skipWhile, switchMap, takeUntil } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { MenuItem } from '../../../private/shared/models/menu-item';
import { TransferDataService } from '../../../private/shared/services/transfer-data.service';
import { TerminosPage } from '../../../private/terminos/terminos.page';
import { AppState } from '../../../store/app-reducers';
import { IUserDataState } from '../../../store/reducers/data-user.reducer';
import { IGNORE } from '../../enums/versiones';
import { HtmlSeguroPipe } from '../../pipes/html-seguro.pipe';
import { AnalyticsService } from '../../services/analytics.service';
import { CreceMasService } from '../../services/crece-mas.service';
import { SharedServiceService } from '../../services/shared-service.service';

@Component({
  selector: 'hamburger-menu',
  templateUrl: './hamburger-menu.component.html',
  styleUrls: ['./hamburger-menu.component.scss'],
})
export class HamburgerMenuComponent implements OnInit, OnDestroy {

  @Input() disabled = false
  @Input() version = ''

  public worldIcon = './assets/icon/world-icon.svg'
  public moreOptions: Array<MenuItem> = [];
  public logout: MenuItem = {
    id: 5,
    tab: 'home',
    title: 'Cerrar sesión',
    iconUrl: './assets/icon/exit-icon-gray.svg',
    data_categoria: 'menu',
    data_accion: '/Mas',
    data_etiqueta: 'cerrar_sesion'
  }

  public isProd = environment.production;
  public versionCode = environment.versionCode;
  public minutes = 0
  private titleE = ''
  private titleO = ''
  private finishSubscriptions: Subject<boolean> = new Subject();

  constructor(
    private menuCtrl: MenuController
    , private shareService: SharedServiceService
    , private analytService: AnalyticsService
    , private transferDataS: TransferDataService
    , private creceMasService: CreceMasService
    , private htmlSeguro: HtmlSeguroPipe
    , private inAppBrowser: InAppBrowser
    , private store: Store<AppState>
    , private nav: NavController
  ) { }

  ngOnInit() {
    this.store.select('userData')
      .pipe(
        switchMap(user => of(user.ID_Bodega).pipe(skipWhile(b => b < 1), map(() => user))),
        takeUntil(this.finishSubscriptions)
      )
      .subscribe(this.callbackUserData);

    this.transferDataS.temporizador()
      .pipe(
        map(value => value.minutes),
        takeUntil(this.finishSubscriptions)
      )
      .subscribe(this.callbackTimer);
  }

  async ngOnDestroy() {
    this.transferDataS.emitTemporizador(false);
    this.finishSubscriptions.next(true);
    this.finishSubscriptions.complete();
    await this.shareService.dismissModalLegales();
  }

  /**
   * Obtiene las opciones del menu
   */
  private callbackUserData = async (User: IUserDataState) => {
    let menu = User.MenuMas;

    this.titleE = menu.filter(x => x.route === '/Autoevaluacion')[0]?.title as string || '';
    this.titleO = menu.filter(x => x.route === '/Autoevaluacion')[0]?.titleOptional as string || '';
    menu = menu.map(x => x.route === '/Autoevaluacion' ? ({ ...x, title: x.titleOptional }) : x);

    const isIgnore = await IGNORE();
    menu = isIgnore ?
      menu.map(x => x.route === '/Games' ? ({ ...x, active: false }) : x)
      : menu;

    this.moreOptions = menu.map(x => ({
      ...x,
      titleText: (String(x.title) || '').split('<')[0].trim() ?? '',
      title: this.htmlSeguro.transform(x.title as string),
      titleOptional: this.htmlSeguro.transform(x.titleOptional as string)
    }));

    const ejecucion = User.Ejecucion.FechasEjecucion;
    if (ejecucion.length > 0) {
      const enTarea = this.transferDataS.betweenEjecucionFinish(ejecucion);
      timer(new Date(ejecucion[0].FechaEjecucionFin.replace(' ', 'T')))
        .pipe(
          skipWhile(() => this.transferDataS.RestanteMinutos(ejecucion[0].FechaEjecucionFin) > 2),
          first(),
          takeUntil(this.finishSubscriptions)
        )
        .subscribe(() => this.transferDataS.emitTemporizador(false));
      if (enTarea) {
        this.transferDataS.emitTemporizador(enTarea);
        return;
      }
      timer(new Date(ejecucion[0].HorarioEjecucion.replace(' ', 'T')))
        .pipe(first(), takeUntil(this.finishSubscriptions))
        .subscribe(() => this.transferDataS.emitTemporizador(true));
    }
  }

  /**
   * Pone el temporizador del menu de foto de exito
   */
  private callbackTimer = (minutes: string) => {
    const titleText = minutes === '0m' ? this.titleO : this.titleE.replace('{{ 0m }}', minutes);
    this.moreOptions = this.moreOptions.map(x => x.route === '/Autoevaluacion' ? ({
      ...x,
      titleText,
      title: this.htmlSeguro.transform(titleText)
    }) : x);
  }

  public sanitizer = (html: string) => this.htmlSeguro.transform(html)

  /**
   * Redirige a un modulo del menu más
   * @param item Objecto del modulo a navegar
   */
  public async navTo(item: MenuItem) {
    const elementText = item.titleText || item.title;
    await this.analytService.sendMegaMenuEvent(elementText, `/${item.tab}`);
    // TODO JAIR: VALIDAR INTERACCION
    this.analytService.fbAnalytics('interacciones', { data_categoria: item.data_categoria, data_accion: item.data_accion, data_etiqueta: item.data_etiqueta })

    switch (item.tab) {
      case 'Terminos':
        this.openTerminos(item)
        await this.menuCtrl.close('first')
        break;
      default:
        this.redirectModule(item);
        break;
    }

    await this.analytService.sendEventMenu(item.tab, 'hamburguer');
  }

  /**
   * Abre Modal de los Terminos y Condiciones
   */
  public async openTerminos(item: MenuItem) {
    await this.shareService.loadingBottle()
    const data = await this.shareService.modalLegales(TerminosPage, item.tipo, item.botonmodal)
    // TODO JAIR: VALIDAR INTERACCION
    this.analytService.fbAnalytics('interacciones', { data_categoria: 'aviso_de_privacidad', data_accion: '/Mas', data_etiqueta: data ? 'aceptar' : 'atras' })
  }

  private redirectModule = async (item: MenuItem) => {
    this.nav.navigateForward([`/${item.tab}`], { animated: true, animationDirection: 'forward' })
    await this.shareService.emitMenu(false)
  }

  public async clearData(item: MenuItem) {
    // AQUI SE CIERRA SESION
    // TODO JAIR: VALIDAR INTERACCION
    this.analytService.fbAnalytics('interacciones', { data_categoria: item.data_categoria, data_accion: item.data_accion, data_etiqueta: item.data_etiqueta })

    await this.menuCtrl.close('first')
    this.shareService.emitMenu(false)
    await this.creceMasService.cerrarSesionCreceMas()

    await this.shareService.clearData(item.tab);
  }

  public openUrl() {
    const browser: InAppBrowserObject = this.inAppBrowser.create('https://gepp.com.mx', '_system')
    // TODO JAIR: VALIDAR INTERACCION
    this.analytService.fbAnalytics('interacciones', { data_categoria: 'menu', data_accion: '/Mas', data_etiqueta: 'url_gepp' })
  }

  public setMenuName = (item: MenuItem) => {
    switch (item.id) {
      case 10:
        return "profile";
      case 16:
        return "games";
      case 20:
        return "rate";
      case 30:
        return "contact";
      case 40:
        return "terms";
      case 50:
        return "privacy";
      case 13:
        return "market-change";
      default:
        return "";
    }
  }

}
